// components/QuizPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuiz } from '../context/QuizContext';
import QuizTimer from './QuizTimer';
import WarningModal from './WarningModal';
import CameraMonitor from './CameraMonitor';
import CheatPrevention from './CheatPrevention';
import QuizQuestions from './QuizQuestions';

const QuizPage = () => {
  const { state, dispatch } = useQuiz();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes

  // 10 Professional Questions
  const questions = [
    {
      id: 1,
      question: "What is the time complexity of binary search in a sorted array?",
      options: ["O(1)", "O(log n)", "O(n)", "O(n log n)"],
      correctAnswer: 1
    },
    {
      id: 2,
      question: "Which data structure uses LIFO (Last In First Out) principle?",
      options: ["Queue", "Stack", "Array", "Linked List"],
      correctAnswer: 1
    },
    {
      id: 3,
      question: "In React, what hook is used to perform side effects?",
      options: ["useState", "useEffect", "useContext", "useReducer"],
      correctAnswer: 1
    },
    {
      id: 4,
      question: "What does CSS stand for?",
      options: [
        "Computer Style Sheets",
        "Creative Style System",
        "Cascading Style Sheets",
        "Colorful Style Sheets"
      ],
      correctAnswer: 2
    },
    {
      id: 5,
      question: "Which protocol is used for secure data transmission over the web?",
      options: ["HTTP", "FTP", "HTTPS", "SMTP"],
      correctAnswer: 2
    },
    {
      id: 6,
      question: "What is the default port for HTTPS?",
      options: ["80", "8080", "443", "22"],
      correctAnswer: 2
    },
    {
      id: 7,
      question: "Which of the following is not a JavaScript data type?",
      options: ["String", "Boolean", "Float", "Symbol"],
      correctAnswer: 2
    },
    {
      id: 8,
      question: "What is the purpose of DNS?",
      options: [
        "Data Network Security",
        "Domain Name System",
        "Digital Network Service",
        "Domain Number System"
      ],
      correctAnswer: 1
    },
    {
      id: 9,
      question: "Which sorting algorithm has the worst-case time complexity of O(n²)?",
      options: ["Merge Sort", "Quick Sort", "Heap Sort", "Bubble Sort"],
      correctAnswer: 3
    },
    {
      id: 10,
      question: "What is the virtual DOM in React?",
      options: [
        "A direct representation of the real DOM",
        "A programming concept where UI is kept in memory",
        "A browser extension for debugging",
        "A CSS framework"
      ],
      correctAnswer: 1
    }
  ];

  useEffect(() => {
    if (state.warnings >= 3) {
      handleAutoSubmit();
    }
  }, [state.warnings]);

  const handleAutoSubmit = () => {
    calculateScore();
    dispatch({ type: 'END_QUIZ' });
    navigate('/results');
  };

  const calculateScore = () => {
    let score = 0;
    state.answers.forEach((answer, index) => {
      if (answer === questions[index]?.correctAnswer) {
        score += 10;
      }
    });
    dispatch({ type: 'SET_SCORE', payload: score });
  };

  const handleTimeUp = () => {
    calculateScore();
    dispatch({ type: 'END_QUIZ' });
    navigate('/results');
  };

  const handleAnswerSelect = (answerIndex) => {
    dispatch({ type: 'SET_ANSWER', payload: answerIndex });
  };

  const handleNext = () => {
    if (state.currentQuestion < questions.length - 1) {
      dispatch({ type: 'SET_CURRENT_QUESTION', payload: state.currentQuestion + 1 });
    }
  };

  const handlePrevious = () => {
    if (state.currentQuestion > 0) {
      dispatch({ type: 'SET_CURRENT_QUESTION', payload: state.currentQuestion - 1 });
    }
  };

  const handleSubmit = () => {
    calculateScore();
    dispatch({ type: 'END_QUIZ' });
    navigate('/results');
  };

  const handleViolation = () => {
    dispatch({ type: 'ADD_WARNING' });
    setShowWarning(true);
    setTimeout(() => setShowWarning(false), 3000);
  };

  if (state.quizCompleted) {
    navigate('/results');
    return null;
  }

  return (
    <div className="quiz-page">
      <CheatPrevention onViolation={handleViolation} />
      <CameraMonitor onCameraLost={handleAutoSubmit} />

      {showWarning && (
        <WarningModal 
          warningCount={state.warnings}
          onClose={() => setShowWarning(false)}
        />
      )}

      <div className="quiz-header">
        <div className="quiz-info">
          <h2>Professional Skills Assessment</h2>
          <div className="student-name">Candidate: {state.studentInfo?.name}</div>
        </div>
        <div className="quiz-controls">
          <div className="warnings">
            ⚠️ Warnings: {state.warnings}/3
          </div>
          <QuizTimer 
            initialTime={timeLeft} 
            onTimeUp={handleTimeUp}
          />
        </div>
      </div>

      <div className="quiz-content">
        <div className="questions-section">
          <QuizQuestions
            questions={questions}
            currentQuestion={state.currentQuestion}
            selectedAnswer={state.answers[state.currentQuestion]}
            onAnswerSelect={handleAnswerSelect}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSubmit={handleSubmit}
            isLastQuestion={state.currentQuestion === questions.length - 1}
          />
        </div>

        <div className="camera-section">
          <div className="camera-feed-small">
            <div className="camera-overlay">
              <span className="recording-indicator"></span>
              <span>Live</span>
            </div>
            <video id="camera-feed" autoPlay playsInline muted />
          </div>
          <div className="quiz-progress">
            <h4>Assessment Progress</h4>
            <div className="progress-bar">
              {questions.map((_, index) => (
                <div
                  key={index}
                  className={`progress-dot ${
                    index === state.currentQuestion
                      ? 'current'
                      : state.answers[index] !== undefined
                      ? 'answered'
                      : 'unanswered'
                  }`}
                />
              ))}
            </div>
            <p>
              Question {state.currentQuestion + 1} of {questions.length}
            </p>
            <div className="completion-status">
              <div 
                className="completion-bar" 
                style={{
                  width: `${((state.currentQuestion + 1) / questions.length) * 100}%`
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;